import { c as _c } from "react/compiler-runtime";
// The generic record (hq Decision web-kind-views: what a kind without a view shows, and what a view may show besides
// its own): its fields as label + value, a reference as a link named after its target, a JSON list of objects as a
// table, then its links and files. Styles: book.css `.fields`, platform.css `.json-table`.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Detail, type Link } from "@/api";
import { errorText } from "@/store";
import { loadName } from "@/views";
import { ErrorLine } from "./error-line";
import { LinkButton } from "./link-button";
const ISO = /^\d{4}-\d\d-\d\dT/;
export const time = (iso: string) => iso.replace("T", " ").slice(0, 16);
const cell = (v: unknown) => v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);

/** `skip`: the keys a view shows itself. Tasks show their links in Context, so `links: false`. */
export function Fields({
  detail,
  onOpen,
  skip = [],
  links: showLinks = true
}: {
  detail: Detail;
  onOpen: (id: string) => void;
  skip?: string[];
  links?: boolean;
}) {
  const {
    t
  } = useTranslation();
  const files = detail.links.filter(l => l.kind === "blob");
  const links = detail.links.filter(l_0 => l_0.kind !== "blob");
  return <dl className="fields">
			{detail.fields.filter(f => !skip.includes(f.key)).map((f_0, i) => <div key={`${f_0.key}${i}`} className="contents">
						<dt>{f_0.key}</dt>
						<dd>{f_0.ref ? <Ref id={f_0.ref} label={f_0.value} onOpen={onOpen} /> : <Value value={f_0.value} />}</dd>
					</div>)}
			{showLinks && links.length > 0 && <>
					<dt>{t("panel.links")}</dt>
					<dd className="mono">
						{links.map(l_1 => <a key={l_1.url} href={l_1.url} target="_blank" rel="noreferrer" className="link-button block truncate">
								{l_1.title ?? l_1.url}
							</a>)}
					</dd>
				</>}
			{files.length > 0 && <>
					<dt>{t("panel.files")}</dt>
					<dd className="mono">
						{files.map(l_2 => <FileLink key={l_2.url} link={l_2} />)}
					</dd>
				</>}
		</dl>;
}

/** A reference: the target's name (its label, else what its kind's view or summary names it), never a bare id. */
export function Ref(t0) {
  const $ = _c(11);
  const {
    id,
    label,
    onOpen
  } = t0;
  const [name, setName] = useState(label && label !== id ? label : null);
  let t1;
  let t2;
  if ($[0] !== id || $[1] !== name) {
    t1 = () => {
      if (!name) {
        loadName(id).then(setName);
      }
    };
    t2 = [id, name];
    $[0] = id;
    $[1] = name;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== id || $[5] !== onOpen) {
    t3 = () => onOpen(id);
    $[4] = id;
    $[5] = onOpen;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = name ?? id;
  let t5;
  if ($[7] !== id || $[8] !== t3 || $[9] !== t4) {
    t5 = <LinkButton title={id} onClick={t3}>{t4}</LinkButton>;
    $[7] = id;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function Value(t0) {
  const $ = _c(13);
  const {
    value
  } = t0;
  if (ISO.test(value)) {
    let t1;
    if ($[0] !== value) {
      t1 = time(value);
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <span className="mono">{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (value.startsWith("[{")) {
    try {
      let t1;
      if ($[4] !== value) {
        t1 = Symbol.for("react.early_return_sentinel");
        bb0: {
          const rows = JSON.parse(value) as Record<string, unknown>[];
          const cols = [...new Set(rows.flatMap(_temp))];
          if (cols.length) {
            const t2 = <tr>{cols.map(_temp2)}</tr>;
            let t3;
            if ($[6] !== t2) {
              t3 = <thead>{t2}</thead>;
              $[6] = t2;
              $[7] = t3;
            } else {
              t3 = $[7];
            }
            const t4 = <tbody>{rows.map((r_0, i) => <tr key={i}>{cols.map(c_0 => <td key={c_0}>{cell(r_0?.[c_0])}</td>)}</tr>)}</tbody>;
            let t5;
            if ($[8] !== t3 || $[9] !== t4) {
              t5 = <div className="json-table"><table>{t3}{t4}</table></div>;
              $[8] = t3;
              $[9] = t4;
              $[10] = t5;
            } else {
              t5 = $[10];
            }
            t1 = t5;
            break bb0;
          }
        }
        $[4] = value;
        $[5] = t1;
      } else {
        t1 = $[5];
      }
      if (t1 !== Symbol.for("react.early_return_sentinel")) {
        return t1;
      }
    } catch {}
  }
  let t1;
  if ($[11] !== value) {
    t1 = <span>{value}</span>;
    $[11] = value;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  return t1;
}
function _temp2(c) {
  return <th key={c}>{c}</th>;
}
function _temp(r) {
  return r && typeof r === "object" ? Object.keys(r) : [];
}
function FileLink(t0) {
  const $ = _c(15);
  const {
    link
  } = t0;
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== link.url) {
    t1 = async () => {
      const win = window.open("", "_blank");
      ;
      try {
        const url = await api.blobs.open(link.url);
        if (win) {
          win.location.href = url;
        } else {
          window.location.href = url;
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        setError(null);
      } catch (t2) {
        const e = t2;
        win?.close();
        setError(errorText(e));
      }
    };
    $[0] = link.url;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const open = t1;
  let t2;
  if ($[2] !== open) {
    t2 = () => void open();
    $[2] = open;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== link.title || $[5] !== link.url) {
    t3 = link.title ?? link.url.split("/").pop();
    $[4] = link.title;
    $[5] = link.url;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <LinkButton className="truncate" onClick={t2}>{t3}</LinkButton>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== error) {
    t5 = error && <ErrorLine inline={true}>{error}</ErrorLine>;
    $[10] = error;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}